"use client";

import React from "react";
import { Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { cn } from "@/lib/utils";
const minimalInputClasses = "border-0 border-b border-secondary/30 rounded-none px-0 py-2 bg-transparent focus-visible:ring-0 focus-visible:border-secondary shadow-none text-primary-foreground placeholder:text-muted-foreground/50";
function InquiryFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="88358" className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in duration-500">
        <div className="size-16 rounded-full bg-secondary/10 flex items-center justify-center mb-4">
          <svg className="size-8 text-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h3 className="text-2xl font-serif text-secondary tracking-wide">Legacy Awaits</h3>
        <p className="text-muted-foreground max-w-md">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-8 rounded-md border-secondary text-secondary hover:bg-secondary hover:text-primary transition-colors uppercase tracking-widest text-xs">
          Submit Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-8">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className="text-xs uppercase tracking-widest text-secondary/70 font-sans">Principal's Name</FormLabel>
              <FormControl>
                <Input placeholder="Alexander Sterling" className={minimalInputClasses} {...field} />
              </FormControl>
              <FormDescription className="text-[10px] text-muted-foreground/60">The primary contact for the estate</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className="text-xs uppercase tracking-widest text-secondary/70 font-sans">Private Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="sterling@kennels.com" className={minimalInputClasses} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        pattern: {
          value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
          message: "Please enter a valid phone number"
        }
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className="text-xs uppercase tracking-widest text-secondary/70 font-sans">Direct Line</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" className={minimalInputClasses} {...field} />
              </FormControl>
              <FormDescription className="text-[10px] text-muted-foreground/60">For private concierge correspondence</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="inquiry_type" rules={{
        required: "Please select an inquiry type"
      }} render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className="text-xs uppercase tracking-widest text-secondary/70 font-sans">Kennel Designation</FormLabel>
              <FormControl>
                <select className={cn("flex h-9 w-full text-base md:text-sm outline-none transition-colors disabled:cursor-not-allowed disabled:opacity-50", minimalInputClasses, !field.value && "text-muted-foreground/50")} {...field}>
                  <option value="" disabled>
                    Select service tier
                  </option>
                  {["Custom Website Digital Home", "Social Media Concierge", "Smart Inquiry System", "Health-First Pedigree Integration", "The Full Estate Management"].map((option, i) => <option key={option} value={option} data-index={i} className="text-primary">
                      {option}
                    </option>)}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <FormField control={control} name="preferred_date" rules={{
          required: "Preferred date is required"
        }} render={({
          field
        }) => <FormItem className="space-y-1">
                <FormLabel className="text-xs uppercase tracking-widest text-secondary/70 font-sans">Anticipated Launch</FormLabel>
                <FormControl>
                  <Input type="date" className={cn(minimalInputClasses, "block w-full")} {...field} />
                </FormControl>
                <FormDescription className="text-[10px] text-muted-foreground/60">Select a target debut date</FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="preferred_time" rules={{
          required: "Preferred time is required"
        }} render={({
          field
        }) => <FormItem className="space-y-1">
                <FormLabel className="text-xs uppercase tracking-widest text-secondary/70 font-sans">Primary Breed Focus</FormLabel>
                <FormControl>
                  <Input type="text" placeholder="e.g. Cavalier King Charles Spaniel" className={minimalInputClasses} {...field} />
                </FormControl>
                <FormDescription className="text-[10px] text-muted-foreground/60">Specify your heritage breed</FormDescription>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="message" render={({
        field
      }) => <FormItem className="space-y-1">
              <FormLabel className="text-xs uppercase tracking-widest text-secondary/70 font-sans">The Vision</FormLabel>
              <FormControl>
                <textarea placeholder="Describe the legacy you wish to build..." className={cn("flex w-full text-base md:text-sm outline-none transition-colors disabled:cursor-not-allowed disabled:opacity-50 min-h-[120px] resize-y", minimalInputClasses)} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 text-destructive text-sm">
            {submitError}
          </div>}

        <div className="pt-4">
          <Button type="submit" disabled={isSubmitting} className="w-full h-14 rounded-md text-sm tracking-[0.2em] uppercase bg-secondary hover:bg-secondary/90 text-primary transition-all font-bold shadow-lg shadow-secondary/10">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-5" />
                Authenticating...
              </> : "Inquire for Availability"}
          </Button>
        </div>
      </div>
    </Form>;
}
export default function InquiryAppointments() {
  return <section id="secure-your-legacy" className="py-24 bg-primary px-6 relative overflow-hidden">
      <div className="max-w-[600px] mx-auto relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-serif text-secondary tracking-tight mb-6">Secure Your Program</h2>
          <div className="w-16 h-[1px] bg-secondary mx-auto opacity-30"></div>
          <p className="mt-8 text-muted-foreground font-sans tracking-wide text-sm max-w-sm mx-auto">Website digital curation for the world's most distinguished breeders and their puppy families.</p>
        </div>

        <FormProvider formId="inquiry_appointment_form_home" sectionName="Inquiry & Appointments" defaultValues={{
        name: "",
        email: "",
        phone: "",
        inquiry_type: "",
        preferred_date: "",
        preferred_time: "",
        message: ""
      }} validationRules={{
        name: {
          required: true
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true,
          pattern: "phone"
        },
        inquiry_type: {
          required: true
        },
        preferred_date: {
          required: true
        },
        preferred_time: {
          required: true
        }
      }} submitText="Inquire for Availability" successMessage="Thank you for your inquiry. A member of our concierge team will contact you shortly to discuss your digital legacy.">
          <InquiryFormContent />
        </FormProvider>
      </div>
      
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[url('https://www.transparenttextures.com/patterns/linen.png')]"></div>
    </section>;
}